import { calgaryDateString } from '../../../../../../utils/calgaryTime';
import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faCheck, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useQuery } from "@tanstack/react-query";
import GenExpensesService from "../../../../../../AppSettings/services/GenExpensesService";
import OppExpService from "../../../../../../AppSettings/services/OppExpService";
import VenExpService from "../../../../../../AppSettings/services/VenExpService";
import FixBillService from "../../../../../../AppSettings/services/FixBillService";
import MatterService from "../../../../../../AppSettings/services/MatterService";
import AgentsService from "../../../../../../AppSettings/services/AgentsService";
import FormEntryMultilineTextbox from "@frontend/Components/Forms/Entry/MultilineTextbox/FormEntryMultilineTextbox";
import FormModal from "@frontend/Components/Modals/FormModal";
import "./LogExpenseEntryModal.css";

type ExpenseType = "general" | "operating" | "vendorServices" | "fixedBills";

interface LogExpenseEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  matterId?: string;
}

const LogExpenseEntryModal: React.FC<LogExpenseEntryModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  matterId,
}) => {
  const [expenseType, setExpenseType] = useState<ExpenseType>("general");
  const [matter, setMatter] = useState<any>(null);
  const [agents, setAgents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Common fields
  const [date, setDate] = useState(calgaryDateString());
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [agentSearch, setAgentSearch] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [taxPercent, setTaxPercent] = useState("0");

  const [showAgentDropdown, setShowAgentDropdown] = useState(false);

  const agentInputRef = useRef<HTMLInputElement>(null);
  const agentDropdownRef = useRef<HTMLDivElement>(null);

  // Fixed Bills catalog state
  const [fbSearch, setFbSearch] = useState("");
  const [showCreateFb, setShowCreateFb] = useState(false);
  const [newFbDescription, setNewFbDescription] = useState("");
  const [newFbAmount, setNewFbAmount] = useState("");
  const [newFbTaxPercent, setNewFbTaxPercent] = useState("0");
  const [fbSelectedAgent, setFbSelectedAgent] = useState<any>(null);
  const [fbAgentSearch, setFbAgentSearch] = useState("");
  const [showFbAgentDropdown, setShowFbAgentDropdown] = useState(false);

  const fbAgentInputRef = useRef<HTMLInputElement>(null);
  const fbAgentDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch fixed bill expenses when Fixed Bills tab is active
  const {
    data: fixedBillsData,
    isLoading: fbLoading,
    error: fbError,
    refetch: refetchFb,
  } = useQuery({
    queryKey: ["fixedBillExpenses"],
    queryFn: FixBillService.getAllFixBillExpenses,
    enabled: isOpen && expenseType === "fixedBills",
  });

  const rawFb = (fixedBillsData as any)?.data ?? fixedBillsData;
  const fbList = Array.isArray(rawFb) ? rawFb : [];
  const filteredFb = fbList.filter((item: any) => {
    if (!fbSearch) return true;
    const q = fbSearch.toLowerCase();
    return (
      item.description?.toLowerCase().includes(q) ||
      item.matter_name?.toLowerCase().includes(q)
    );
  });

  // Fetch matter and agents data
  useEffect(() => {
    const fetchData = async () => {
      if (matterId) {
        try {
          const matterData = await MatterService.getMatterName(matterId);
          if (matterData) {
            setMatter({
              matterid: matterId,
              mattername: matterData.mattername || matterId,
            });
          }
        } catch (error) {
          console.error("Error fetching matter:", error);
        }
      }

      try {
        const agentsData = await AgentsService.getAgents();
        setAgents(agentsData || []);
      } catch (error) {
        console.error("Error fetching agents:", error);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen, matterId]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setExpenseType("general");
      setSelectedAgent(null);
      setAgentSearch("");
      setDescription("");
      setAmount("");
      setTaxPercent("0");
      setDate(calgaryDateString());
      setError("");
      setShowAgentDropdown(false);
      setFbSearch("");
      setShowCreateFb(false);
      setNewFbDescription("");
      setNewFbAmount("");
      setNewFbTaxPercent("0");
      setFbSelectedAgent(null);
      setFbAgentSearch("");
      setShowFbAgentDropdown(false);
    }
  }, [isOpen]);

  // Close dropdown when clicking outside (main form)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        agentDropdownRef.current &&
        agentInputRef.current &&
        !agentDropdownRef.current.contains(event.target as Node) &&
        !agentInputRef.current.contains(event.target as Node)
      ) {
        setShowAgentDropdown(false);
      }
      if (
        fbAgentDropdownRef.current &&
        fbAgentInputRef.current &&
        !fbAgentDropdownRef.current.contains(event.target as Node) &&
        !fbAgentInputRef.current.contains(event.target as Node)
      ) {
        setShowFbAgentDropdown(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const getAgentDisplayName = (agent: any): string => {
    let displayName = "";
    if (agent.FirstName && agent.LastName) {
      displayName = `${agent.FirstName} ${agent.LastName}`;
    } else if (agent.FirstName && !agent.LastName) {
      displayName = agent.FirstName;
    } else if (!agent.FirstName && agent.LastName) {
      displayName = agent.LastName;
    } else if (agent.ContactName) {
      displayName = agent.ContactName;
    } else {
      displayName = "Unknown Agent";
    }
    return `${displayName}${agent.Company ? ` - ${agent.Company}` : ""}`;
  };

  const filteredAgents = agents.filter((agent) =>
    getAgentDisplayName(agent)
      .toLowerCase()
      .includes(agentSearch.toLowerCase())
  );

  const filteredFbAgents = agents.filter((agent) =>
    getAgentDisplayName(agent)
      .toLowerCase()
      .includes(fbAgentSearch.toLowerCase())
  );

  const handleAgentSelect = (agent: any) => {
    setSelectedAgent(agent);
    setAgentSearch(getAgentDisplayName(agent));
    setShowAgentDropdown(false);
  };

  const handleFbAgentSelect = (agent: any) => {
    setFbSelectedAgent(agent);
    setFbAgentSearch(getAgentDisplayName(agent));
    setShowFbAgentDropdown(false);
  };

  const handleAssignFixedBill = async (item: any) => {
    if (!matterId || !matter) {
      setError("Matter information is missing");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await FixBillService.updateFixBillExpense(String(item.fbill_id), {
        description: item.description,
        amount: item.amount,
        tax_percent: item.tax_percent || 0,
        agentid: item.agentid,
        matterid: matterId,
        date_added: item.date_added || calgaryDateString(),
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Error assigning fixed bill:", err);
      setError(err.message || "Failed to assign fixed bill.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFixedBill = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fbSelectedAgent) {
      setError("Please select an agent");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await FixBillService.addFixBillExpense({
        description: newFbDescription,
        amount: parseFloat(newFbAmount),
        tax_percent: parseFloat(newFbTaxPercent) || 0,
        agentid: fbSelectedAgent.AgentID,
        date_added: calgaryDateString(),
      });
      setNewFbDescription("");
      setNewFbAmount("");
      setNewFbTaxPercent("0");
      setFbSelectedAgent(null);
      setFbAgentSearch("");
      setShowCreateFb(false);
      refetchFb();
    } catch (err: any) {
      console.error("Error creating fixed bill:", err);
      setError(err.message || "Failed to create fixed bill.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (!matterId || !matter) {
        setError("Matter information is missing");
        setLoading(false);
        return;
      }

      if (!selectedAgent) {
        setError("Please select an agent");
        setLoading(false);
        return;
      }

      if (!description || !amount) {
        setError("Please fill in all required fields");
        setLoading(false);
        return;
      }

      const formData: any = {
        description,
        amount: parseFloat(amount),
        tax_percent: parseFloat(taxPercent) || 0,
        date_added: date,
        agentid: selectedAgent.AgentID,
        matterid: matterId,
      };

      if (expenseType === "general") {
        formData.invoiceid = null;
        await GenExpensesService.addGenExpense(formData);
      } else if (expenseType === "operating") {
        await OppExpService.addOppExpense(formData);
      } else if (expenseType === "vendorServices") {
        await VenExpService.addVenExpense(formData);
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error adding expense entry:", error);
      setError(
        error.message || "Failed to add expense entry. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Log Expense Entry"
      size="medium"
    >
      {/* Expense Type Selection */}
      <div className="md-log-expense-entry-type-tabs">
        <button
          className={`md-log-expense-entry-tab ${
            expenseType === "general" ? "md-active" : ""
          }`}
          onClick={() => setExpenseType("general")}
        >
          General
        </button>
        <button
          className={`md-log-expense-entry-tab ${
            expenseType === "operating" ? "md-active" : ""
          }`}
          onClick={() => setExpenseType("operating")}
        >
          Operating
        </button>
        <button
          className={`md-log-expense-entry-tab ${
            expenseType === "vendorServices" ? "md-active" : ""
          }`}
          onClick={() => setExpenseType("vendorServices")}
        >
          Vendor Services
        </button>
        <button
          className={`md-log-expense-entry-tab ${
            expenseType === "fixedBills" ? "md-active" : ""
          }`}
          onClick={() => setExpenseType("fixedBills")}
        >
          Fixed Bills
        </button>
      </div>

      {error && <div className="md-log-expense-entry-error">{error}</div>}

      {/* Fixed Bills tab: clickable catalog list */}
      {expenseType === "fixedBills" && (
        <div className="md-log-expense-fb-section">
          <div className="md-log-expense-entry-field">
            <label>Matter</label>
            <input
              type="text"
              value={matter ? `${matter.matterid}: ${matter.mattername || ""}` : "Loading..."}
              disabled
              className="md-log-expense-entry-disabled-input"
            />
          </div>

          <div className="md-log-expense-entry-field">
            <label>Search Fixed Bills</label>
            <input
              type="text"
              value={fbSearch}
              onChange={(e) => setFbSearch(e.target.value)}
              placeholder="Filter by description..."
            />
          </div>

          <div className="md-log-expense-fb-create-toggle">
            <button
              type="button"
              className="md-log-expense-fb-create-btn"
              onClick={() => setShowCreateFb(!showCreateFb)}
            >
              {showCreateFb ? "Cancel" : "Create New Fixed Bill"}
            </button>
          </div>

          {showCreateFb && (
            <form
              className="md-log-expense-fb-create-form"
              onSubmit={handleCreateFixedBill}
            >
              <div className="md-log-expense-entry-field">
                <label>Agent</label>
                <div className="md-log-expense-entry-search-container">
                  <input
                    ref={fbAgentInputRef}
                    type="text"
                    placeholder="Search agents..."
                    value={fbAgentSearch}
                    onChange={(e) => {
                      setFbAgentSearch(e.target.value);
                      setShowFbAgentDropdown(true);
                    }}
                    onFocus={() => setShowFbAgentDropdown(true)}
                    required
                  />
                  <FontAwesomeIcon icon={faSearch} className="md-search-icon" />
                  {showFbAgentDropdown && filteredFbAgents.length > 0 && (
                    <div ref={fbAgentDropdownRef} className="md-log-expense-entry-dropdown">
                      {filteredFbAgents.slice(0, 10).map((agent) => (
                        <div
                          key={agent.AgentID}
                          className="md-log-expense-entry-dropdown-item"
                          onClick={() => handleFbAgentSelect(agent)}
                        >
                          {getAgentDisplayName(agent)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="md-log-expense-entry-field">
                <label>Description</label>
                <input
                  type="text"
                  value={newFbDescription}
                  onChange={(e) => setNewFbDescription(e.target.value)}
                  placeholder="e.g. Monthly Office Rent"
                  required
                />
              </div>
              <div className="md-log-expense-entry-field">
                <label>Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newFbAmount}
                  onChange={(e) => setNewFbAmount(e.target.value)}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="md-log-expense-entry-field">
                <label>Tax Percent (Optional)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={newFbTaxPercent}
                  onChange={(e) => setNewFbTaxPercent(e.target.value)}
                  placeholder="0"
                />
              </div>
              <button
                type="submit"
                className="md-log-expense-entry-save"
                disabled={loading}
              >
                <FontAwesomeIcon icon={faCheck} />
                Save Fixed Bill
              </button>
            </form>
          )}

          {fbLoading && (
            <p className="md-log-expense-entry-hint">Loading fixed bills...</p>
          )}
          {fbError && (
            <div className="md-log-expense-entry-error">Error loading fixed bills.</div>
          )}

          <div className="md-log-expense-fb-list">
            {filteredFb.length === 0 && !fbLoading && (
              <p className="md-log-expense-fb-empty">No fixed bills found. Create one above to get started.</p>
            )}
            {filteredFb.map((item: any) => (
              <div
                key={item.fbill_id}
                className="md-log-expense-fb-item"
              >
                <div className="md-log-expense-fb-item-info">
                  <span className="md-log-expense-fb-description">{item.description}</span>
                  {item.matter_name && (
                    <span className="md-log-expense-fb-matter">{item.matter_name}</span>
                  )}
                </div>
                <div className="md-log-expense-fb-item-right">
                  <span className="md-log-expense-fb-amount">${item.amount}</span>
                  <button
                    type="button"
                    className="md-log-expense-fb-assign-btn"
                    disabled={loading}
                    onClick={() => handleAssignFixedBill(item)}
                    title="Assign to this matter"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form for General, Operating, and Vendor Services tabs */}
      {expenseType !== "fixedBills" && (
        <form onSubmit={handleSubmit} className="md-log-expense-entry-form">
          {/* Common Fields */}
          <div className="md-log-expense-entry-field">
            <label>Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>

          <div className="md-log-expense-entry-field">
            <label>Matter</label>
            <input
              type="text"
              value={matter ? `${matter.matterid}: ${matter.mattername || ""}` : "Loading..."}
              disabled
              className="md-log-expense-entry-disabled-input"
            />
            <p className="md-log-expense-entry-hint">
              Matter is pre-selected based on current context.
            </p>
          </div>

          <div className="md-log-expense-entry-field">
            <label>Agent</label>
            <div className="md-log-expense-entry-search-container">
              <input
                ref={agentInputRef}
                type="text"
                placeholder="Search agents..."
                value={agentSearch}
                onChange={(e) => {
                  setAgentSearch(e.target.value);
                  setShowAgentDropdown(true);
                }}
                onFocus={() => setShowAgentDropdown(true)}
                required
              />
              <FontAwesomeIcon icon={faSearch} className="md-search-icon" />
              {showAgentDropdown && filteredAgents.length > 0 && (
                <div ref={agentDropdownRef} className="md-log-expense-entry-dropdown">
                  {filteredAgents.slice(0, 10).map((agent) => (
                    <div
                      key={agent.AgentID}
                      className="md-log-expense-entry-dropdown-item"
                      onClick={() => handleAgentSelect(agent)}
                    >
                      {getAgentDisplayName(agent)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="md-log-expense-entry-field">
            <label>Description</label>
            <FormEntryMultilineTextbox
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter expense description..."
              rows={4}
            />
          </div>

          <div className="md-log-expense-entry-field">
            <label>Amount</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
            />
          </div>

          <div className="md-log-expense-entry-field">
            <label>Tax Percent (Optional)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={taxPercent}
              onChange={(e) => setTaxPercent(e.target.value)}
              placeholder="0"
            />
          </div>

          {/* Action Buttons */}
          <div className="md-log-expense-entry-actions">
            <button
              type="button"
              className="md-log-expense-entry-cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="md-log-expense-entry-save"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faCheck} />
              Save to Queue
            </button>
          </div>
        </form>
      )}
    </FormModal>
  );
};

export default LogExpenseEntryModal;
